import { isNetworkErrorCode } from '@opositandopn/http';
import { Variant } from '@opositandopn/ui';
import { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';

import { markNotified } from '../utils/api';

interface RetryableRequestConfig extends AxiosRequestConfig {
  __isRetryRequest?: boolean;
}

export type ResponseErrorHandlerDeps = {
  instance: AxiosInstance;
  getIdToken: (forceRefresh: boolean) => Promise<string>;
  sendMessage: (title: string, message: string, variant?: Variant) => void;
  setShowGetPremiumPopup: (show: boolean) => void;
  setShowQuotaExceededPopup: (show: boolean) => void;
};

/**
 * Handles the failures only this layer can resolve: a stale token, a
 * subscription gate, a spent quota, a dead connection. Everything else is a
 * single request failing, which the query client reports — see
 * `notifyError`.
 */
export function createResponseErrorHandler({
  instance,
  getIdToken,
  sendMessage,
  setShowGetPremiumPopup,
  setShowQuotaExceededPopup,
}: ResponseErrorHandlerDeps) {
  return async (error: AxiosError) => {
    // Silencing is decided once here rather than per branch, so every way this
    // handler has of talking to the user goes quiet at once and a case added
    // later is silent by construction. Retrying is not talking to the user, so
    // the 401 refresh below still runs for a silent request.
    const silence = error.config?.silent === true;

    if (silence) {
      markNotified(error);
    }

    function announce<TArgs extends unknown[]>(fn: (...args: TArgs) => void) {
      return (...args: TArgs) => {
        if (silence) {
          return;
        }

        markNotified(error);
        fn(...args);
      };
    }

    const notify = announce(sendMessage);
    const openPremiumPopup = announce(setShowGetPremiumPopup);
    const openQuotaPopup = announce(setShowQuotaExceededPopup);

    if (isNetworkErrorCode(error.code)) {
      notify(
        '¡Ups!',
        'Parece que ha ocurrido un error con la conexión de red.',
        'danger',
      );

      return Promise.reject(error);
    }

    if (error.response) {
      const { config } = error;
      const { status } = error.response;

      switch (status) {
        case 401:
          if (config && !(config as RetryableRequestConfig).__isRetryRequest) {
            (config as RetryableRequestConfig).__isRetryRequest = true;

            try {
              config.headers.Authorization = `Bearer ${await getIdToken(true)}`;

              return instance.request(config);
            } catch (refreshError) {
              // The refresh is the one call here that can fail on its own terms,
              // and letting it throw replaces the 401 every caller is prepared to
              // read with a Firebase error nobody upstream recognises. Falls through
              // to the message below, and the original error is what gets rejected.
              console.error(
                'Could not refresh the token after a 401.',
                refreshError,
              );
            }
          }

          // Reached only once the retry is spent: a successful one returns above.
          notify(
            'No tienes permisos para realizar esta acción',
            'No tienes permisos para realizar esta acción. Por favor, contacta con el administrador del sistema.',
            'danger',
          );
          break;

        case 403:
          openPremiumPopup(true);
          break;

        case 429: {
          const code = (error.response.data as { code?: string })?.code;

          if (code === 'rate_limit_exceeded') {
            notify(
              '¡Ups!',
              'Parece que has excedido el límite de uso permitido. Si el problema persiste escríbenos a info@opositandopn.com',
              'danger',
            );
          } else if (code === 'quota_exceeded') {
            openQuotaPopup(true);
          }
          break;
        }
      }
    }

    return Promise.reject(error);
  };
}
